<template>
  <main class="voting-page voting-confirmation-page">
    <section class="voting-confirmation-card">
      <div class="voting-confirmation-card__icon" aria-hidden="true">&#10003;</div>
      <p class="voting-page__eyebrow">Voto confirmado</p>
      <h1>&iexcl;Gracias por participar!</h1>
      <p class="voting-confirmation-card__intro">
        Tu boleta fue registrada correctamente. Tus selecciones ya no pueden modificarse.
      </p>

      <div v-if="percentage !== null" class="voting-confirmation-card__metric">
        <span>Participación acumulada</span>
        <strong>{{ percentage }}%</strong>
      </div>
    </section>

    <section class="voting-confirmation-details">
      <article v-if="closeDate" class="voting-detail-card">
        <span class="voting-detail-card__label">Cierre</span>
        <strong>{{ closeDate }}</strong>
      </article>
      <article v-if="publishDate || officialAnnouncementChannel" class="voting-detail-card">
        <span class="voting-detail-card__label">Resultados</span>
        <strong v-if="publishDate">{{ publishDate }}</strong>
        <span v-if="officialAnnouncementChannel">{{ officialAnnouncementChannel }}</span>
      </article>
    </section>

    <section v-if="shareUrl" class="voting-results-card">
      <p class="voting-detail-card__label">Resultados públicos</p>
      <p>
        {{
          resultsPublished
            ? "Consulta el resultado oficial de esta votación."
            : publishDate
              ? `Los resultados estarán disponibles el ${publishDate}.`
              : "Guarda este enlace. Los resultados aparecerán automáticamente cuando estén disponibles."
        }}
      </p>
      <a :href="shareUrl" target="_blank" rel="noopener noreferrer">
        {{ shareUrl }} <span aria-hidden="true">&#8599;</span>
      </a>
    </section>

    <router-link class="voting-button voting-button--secondary" :to="dashboardRoute">
      Volver a mis votaciones
    </router-link>
  </main>
</template>

<script>
import {
  formatVotingDate,
  getLocalVotingResultsUrl,
  parseVotingQueryValue,
} from "@/share/voting";

export default {
  name: "MemberVotingConfirmation",
  computed: {
    isV2Route() {
      return this.$route.path.indexOf("/member-v2/") === 0;
    },
    dashboardRoute() {
      return {
        name: this.isV2Route ? "Member V2 Voting" : "Member Voting",
      };
    },
    participation() {
      try {
        return JSON.parse(parseVotingQueryValue(this.$route.query.participation) || "{}");
      } catch (error) {
        return {};
      }
    },
    percentage() {
      const value = this.participation.percentage ?? this.participation.participation;
      return value === undefined || value === null ? null : value;
    },
    closeDate() {
      return this.formatDate(this.$route.query.closesAt);
    },
    publishDate() {
      return this.formatDate(this.$route.query.resultsPublishAt);
    },
    officialAnnouncementChannel() {
      return parseVotingQueryValue(this.$route.query.officialAnnouncementChannel);
    },
    shareUrl() {
      return getLocalVotingResultsUrl(
        parseVotingQueryValue(this.$route.query.shareUrl)
      );
    },
    resultsPublished() {
      return parseVotingQueryValue(this.$route.query.resultsPublished) === "true";
    },
  },
  methods: {
    formatDate(value) {
      return value ? formatVotingDate(parseVotingQueryValue(value)) : "";
    },
  },
};
</script>

<style scoped>
.voting-page {
  min-height: calc(100vh - 56px);
  box-sizing: border-box;
  max-width: 820px;
  margin: 0 auto;
  padding: 42px 28px 64px;
  color: #1b273e;
  text-align: center;
}

.voting-confirmation-card,
.voting-detail-card,
.voting-results-card {
  border: 1px solid #dfe4eb;
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 8px 22px rgba(27, 39, 62, 0.06);
}

.voting-confirmation-card {
  padding: 38px 24px;
}

.voting-confirmation-card__icon {
  display: flex;
  width: 76px;
  height: 76px;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  border-radius: 50%;
  background: #e7f6ea;
  color: #237b37;
  font-size: 34px;
  font-weight: 900;
}

.voting-page__eyebrow {
  margin: 22px 0 0;
  color: #a66f00;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.voting-confirmation-card h1 {
  margin: 8px 0 0;
  font-size: clamp(28px, 4vw, 40px);
  line-height: 1.15;
}

.voting-confirmation-card__intro {
  max-width: 520px;
  margin: 12px auto 0;
  color: #687386;
  font-size: 15px;
  line-height: 1.65;
}

.voting-confirmation-card__metric {
  max-width: 280px;
  margin: 26px auto 0;
  border-radius: 14px;
  padding: 16px;
  background: #f5f7fa;
}

.voting-confirmation-card__metric span,
.voting-confirmation-card__metric strong {
  display: block;
}

.voting-confirmation-card__metric span {
  color: #687386;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.voting-confirmation-card__metric strong {
  margin-top: 5px;
  color: #a66f00;
  font-size: 36px;
}

.voting-confirmation-details {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 16px;
  text-align: left;
}

.voting-detail-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 18px;
}

.voting-detail-card__label {
  margin: 0;
  color: #687386;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.voting-detail-card strong,
.voting-detail-card span:not(.voting-detail-card__label) {
  color: #1b273e;
  font-size: 14px;
}

.voting-results-card {
  margin-top: 16px;
  padding: 18px;
  text-align: left;
}

.voting-results-card p:not(.voting-detail-card__label) {
  margin: 8px 0 0;
  color: #687386;
  font-size: 14px;
  line-height: 1.55;
}

.voting-results-card a {
  display: inline-block;
  max-width: 100%;
  margin-top: 12px;
  overflow-wrap: anywhere;
  color: #1b273e;
  font-size: 14px;
  font-weight: 800;
}

.voting-button {
  display: inline-flex;
  min-height: 46px;
  align-items: center;
  justify-content: center;
  margin-top: 22px;
  border-radius: 999px;
  padding: 11px 18px;
  font-size: 14px;
  font-weight: 800;
  text-decoration: none;
}

.voting-button--secondary {
  border: 1px solid #cbd3df;
  background: #ffffff;
  color: #1b273e;
}

@media (max-width: 600px) {
  .voting-page {
    padding: 24px 16px 48px;
  }

  .voting-confirmation-details {
    grid-template-columns: 1fr;
  }

  .voting-button {
    width: 100%;
  }
}
</style>
